{
/**
 * <bz-slider> — carrusel ligero con scroll-snap (sin librerías).
 *
 * Marcado:
 *   <bz-slider dots arrows autoplay="5">
 *     <div class="bz-slider__track" data-bz-track>
 *       <div class="bz-slider__slide">…</div>
 *       …
 *     </div>
 *   </bz-slider>
 *
 * Atributos:
 *   dots            muestra puntos de paginación
 *   arrows          muestra flechas anterior/siguiente
 *   autoplay="N"    avanza cada N segundos (0 o ausente = apagado). Se pausa al pasar el
 *                   ratón o enfocar; se detiene para siempre tras tocar/arrastrar/pulsar un control.
 *                   Nunca arranca con prefers-reduced-motion ni con la pestaña oculta/fuera de pantalla.
 *   label-prev / label-next / label-dot   textos accesibles ("{n}" se sustituye en label-dot).
 *
 * API: goTo(i), next(), prev(), refresh(); propiedades index y count.
 * Evento: "bz-slider:change" (bubbles) con detail { index, count }.
 * Los controles se ocultan solos cuando no hay desbordamiento (p. ej. rejilla en escritorio).
 */
class BzSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-bz-track]');
    if (!this.track) return;
    this.index = 0;
    this.count = 1;
    this._stopped = false;
    this._hover = false;
    this._visible = true;
    this._reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._onScroll = () => {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = 0;
        this._sync();
      });
    };
    this.track.addEventListener('scroll', this._onScroll, { passive: true });
    this._buildNav();
    this._ro = new ResizeObserver(() => this.refresh());
    this._ro.observe(this.track);
    this.refresh();
    this._setupAutoplay();
  }

  disconnectedCallback() {
    if (!this.track) return;
    this.track.removeEventListener('scroll', this._onScroll);
    if (this._ro) this._ro.disconnect();
    if (this._io) this._io.disconnect();
    this._clearTimer();
  }

  get slides() {
    return Array.from(this.track.children);
  }

  _label(name, fallback, n) {
    const v = this.getAttribute('label-' + name) || fallback;
    return n === undefined ? v : v.replace('{n}', n);
  }

  _buildNav() {
    const wantDots = this.hasAttribute('dots');
    const wantArrows = this.hasAttribute('arrows');
    if (!wantDots && !wantArrows) return;
    const nav = document.createElement('div');
    nav.className = 'bz-slider__nav';
    nav.hidden = true;
    if (wantArrows) {
      this._prev = this._arrow('prev', this._label('prev', 'Anterior'));
      nav.appendChild(this._prev);
    }
    if (wantDots) {
      this._dots = document.createElement('div');
      this._dots.className = 'bz-slider__dots';
      nav.appendChild(this._dots);
    }
    if (wantArrows) {
      this._next = this._arrow('next', this._label('next', 'Siguiente'));
      nav.appendChild(this._next);
    }
    this.appendChild(nav);
    this._nav = nav;
  }

  _arrow(dir, label) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'bz-slider__arrow bz-slider__arrow--' + dir;
    b.setAttribute('aria-label', label);
    b.innerHTML =
      '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="' +
      (dir === 'prev' ? 'M15.4 6 14 4.6 6.6 12 14 19.4 15.4 18 9.4 12z' : 'M8.6 6 10 4.6 17.4 12 10 19.4 8.6 18l6-6z') +
      '" fill="currentColor"/></svg>';
    b.addEventListener('click', () => {
      this._stop();
      dir === 'prev' ? this.prev() : this.next();
    });
    return b;
  }

  _gap() {
    const cs = getComputedStyle(this.track);
    return parseFloat(cs.columnGap || cs.gap) || 0;
  }

  refresh() {
    if (!this.track) return;
    const slides = this.slides;
    const t = this.track;
    const overflow = t.scrollWidth - t.clientWidth > 2;
    let count = 1;
    if (overflow && slides.length) {
      const w = slides[0].offsetWidth + this._gap();
      const visible = Math.max(1, Math.floor((t.clientWidth + this._gap()) / (w || 1) + 0.02));
      count = Math.max(1, slides.length - visible + 1);
    }
    if (count !== this.count || !this._built) {
      this.count = count;
      this._built = true;
      if (this._dots) {
        this._dots.textContent = '';
        for (let i = 0; i < count; i++) {
          const d = document.createElement('button');
          d.type = 'button';
          d.className = 'bz-slider__dot';
          d.setAttribute('aria-label', this._label('dot', 'Ir a la diapositiva {n}', i + 1));
          d.addEventListener('click', () => {
            this._stop();
            this.goTo(i);
          });
          this._dots.appendChild(d);
        }
      }
    }
    if (this._nav) this._nav.hidden = count <= 1;
    this._sync();
  }

  _sync() {
    const t = this.track;
    const slides = this.slides;
    if (!slides.length) return;
    const max = t.scrollWidth - t.clientWidth;
    let idx = 0;
    if (max > 2 && t.scrollLeft >= max - 2) {
      idx = this.count - 1;
    } else {
      let best = Infinity;
      slides.forEach((s, i) => {
        const d = Math.abs(s.offsetLeft - t.scrollLeft);
        if (d < best) {
          best = d;
          idx = i;
        }
      });
      idx = Math.min(idx, this.count - 1);
    }
    const changed = idx !== this.index;
    this.index = idx;
    if (this._dots) {
      Array.from(this._dots.children).forEach((d, i) => {
        if (i === idx) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
    }
    if (this._prev) this._prev.disabled = idx <= 0;
    if (this._next) this._next.disabled = idx >= this.count - 1;
    if (changed) {
      this.dispatchEvent(new CustomEvent('bz-slider:change', { bubbles: true, detail: { index: idx, count: this.count } }));
    }
  }

  goTo(i) {
    const slides = this.slides;
    if (!slides.length) return;
    const n = Math.max(0, Math.min(i, slides.length - 1));
    this.track.scrollTo({ left: slides[n].offsetLeft, behavior: this._reduce.matches ? 'auto' : 'smooth' });
  }

  next() {
    this.goTo(Math.min(this.index + 1, this.count - 1));
  }

  prev() {
    this.goTo(Math.max(this.index - 1, 0));
  }

  /* ---------- autoplay ---------- */
  _setupAutoplay() {
    const secs = parseFloat(this.getAttribute('autoplay'));
    if (!secs || secs <= 0) return;
    this._delay = Math.max(2, secs) * 1000;
    const stopEvents = ['touchstart', 'pointerdown', 'wheel', 'keydown'];
    stopEvents.forEach((ev) => this.track.addEventListener(ev, () => this._stop(), { passive: true }));
    this.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'mouse') this._hover = true;
    });
    this.addEventListener('pointerleave', () => (this._hover = false));
    this.addEventListener('focusin', () => (this._hover = true));
    this.addEventListener('focusout', () => (this._hover = false));
    this._io = new IntersectionObserver((entries) => {
      this._visible = entries[0].isIntersecting;
    });
    this._io.observe(this);
    this._reduce.addEventListener('change', () => (this._reduce.matches ? this._clearTimer() : this._start()));
    this._start();
  }

  _start() {
    if (this._stopped || this._reduce.matches || !this._delay) return;
    this._clearTimer();
    this._timer = setInterval(() => {
      if (this._hover || document.hidden || !this._visible || this.count <= 1) return;
      this.goTo(this.index >= this.count - 1 ? 0 : this.index + 1);
    }, this._delay);
  }

  _clearTimer() {
    if (this._timer) clearInterval(this._timer);
    this._timer = null;
  }

  _stop() {
    this._stopped = true;
    this._clearTimer();
  }
}

if (!customElements.get('bz-slider')) customElements.define('bz-slider', BzSlider);
}
